import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import apiService from "../Common/Configurations/APIService";
import './Login.css';
import config from "../Common/Configurations/APIConfig";
import { useAuth } from "../Common/Configurations/AuthContext";
import DefaultData from '../CompanyData/DefaultData';

const keyHighlights = [
  { value: '5000+', label: 'Flight Routes Worldwide', icon: 'ri-plane-line' },
  { value: '400,000+', label: 'Hotel & Apartment Rooms', icon: 'ri-building-line' },
  { value: '1000+', label: 'Directly Contracted Hotels', icon: 'ri-hotel-line' },
  { value: '20,000+', label: 'Sightseeing & Tours', icon: 'ri-map-pin-line' },
];

const services = [
  { icon: 'ri-plane-line', title: 'Flights', desc: 'Access 130+ airlines with exclusive B2B fares and instant ticketing.', color: '#2D93D3' },
  { icon: 'ri-building-line', title: 'Hotels', desc: '400K+ rooms from global chains with negotiated corporate rates.', color: '#e67e22' },
  { icon: 'ri-suitcase-line', title: 'Holiday Packages', desc: 'Curated, customizable packages for leisure and group travel.', color: '#27ae60' },
  { icon: 'ri-moon-line', title: 'Umrah', desc: 'Complete Umrah packages including visa, transport & accommodation.', color: '#8e44ad' },
  { icon: 'ri-star-line', title: 'Hajj', desc: 'Premium Hajj packages with full logistical support.', color: '#c0392b' },
  { icon: 'ri-map-pin-line', title: 'Sightseeing & Tours', desc: '20,000+ experiences and guided tours across the globe.', color: '#16a085' },
];

const agentBenefits = [
  { icon: 'ri-global-line', title: 'Global Inventory', text: 'Access 130+ airlines and 400,000+ hotels with contracted rates unavailable to the public.' },
  { icon: 'ri-money-dollar-circle-line', title: 'Competitive Commissions', text: 'Earn attractive commissions with transparent pricing and exclusive agent-only deals and promotions.' },
  { icon: 'ri-cpu-line', title: 'Smart Technology', text: 'Advanced tools with real-time availability, instant confirmation, automated ticketing and invoicing.' },
  { icon: 'ri-customer-service-2-line', title: '24/7 Expert Support', text: 'Dedicated agent support team available around the clock for bookings, changes, and emergencies.' },
];

const steps = [
  { step: '01', icon: 'ri-user-add-line', title: 'Register as Agent', desc: 'Complete our quick agent registration form with your travel agency details. Takes less than 5 minutes.' },
  { step: '02', icon: 'ri-shield-check-line', title: 'Get Verified', desc: 'Our team reviews your application and verifies your credentials within 24 hours.' },
  { step: '03', icon: 'ri-rocket-2-line', title: 'Start Earning', desc: 'Access our full B2B portal, start booking for your clients, and earn commissions immediately.' },
];

const partners = [
  { name: 'Amadeus', logo: '/assets/images/amadeus.png' },
  { name: 'Sabre', logo: '/assets/images/sabre.png' },
  { name: 'JacTravel', logo: '/assets/images/jactravel.png' },
  { name: 'Restel', logo: '/assets/images/restel.png' },
];

const accreditations = [
  { name: 'IATA', logo: '/assets/images/iata.png' },
  { name: 'MATTA', logo: '/assets/images/matta.png' },
  { name: 'PATA', logo: '/assets/images/pata.png' },
  { name: 'IAGTO', logo: '/assets/images/iagto.png' },
];

const initialLoginValues = {
  username: '',
  password: '',
  companyId: DefaultData.DEFAULTCOMPANYID,
};

export default function Login() {
  const { login } = useAuth();
  const [values, setValues] = useState(initialLoginValues);
  const [errors, setErrors] = useState({});
  const [btnSubmit, setBtnSubmit] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
  };

  const validate = () => {
    const temp = {
      username: values.username !== '',
      password: values.password !== '',
    };
    setErrors(temp);
    return Object.values(temp).every((x) => x === true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setBtnSubmit(true);
      try {
        const result = await apiService.post(config.LOGIN, {
          username: values.username,
          password: values.password,
          companyId: DefaultData.DEFAULTCOMPANYID,
        });
        login({ accessToken: result.accessToken, refreshToken: result.refreshToken });
      } catch (error) {
        console.error('Login failed:', error);
      } finally {
        setBtnSubmit(false);
      }
    }
  };

  const applyErrorClass = (field) =>
    field in errors && errors[field] === false ? ' form-control-danger' : '';

  const pageThemeStyles = {
    '--theme-primary-color': DefaultData.THEMECOLOR1,
    '--theme-secondary-color': DefaultData.THEMECOLOR2,
  };

  return (
    <div style={pageThemeStyles} className="homepage-wrapper">

      {/* ===== HERO SECTION ===== */}
      <section className="hp-hero-section">
        <div className="hp-hero-bg-orbs"></div>
        <Container>
          <Row className="align-items-center hp-hero-row">

            {/* Left: Branding & Info */}
            <Col lg={7} className="hp-hero-left">
              <div className="hp-hero-badge">
                <i className="ri-shield-star-line"></i>
                Trusted B2B Travel Platform
              </div>
              <h1 className="hp-hero-headline">
                {DefaultData.LANDINGPAGETITLE}
              </h1>
              <p className="hp-hero-subtext">{DefaultData.LANDINGPAGESUBTITLE}</p>
              <div className="hp-service-pills">
                {[
                  { icon: 'ri-plane-line', label: 'Flights' },
                  { icon: 'ri-building-line', label: 'Hotels' },
                  { icon: 'ri-suitcase-line', label: 'Holidays' },
                  { icon: 'ri-moon-line', label: 'Umrah' },
                  { icon: 'ri-star-line', label: 'Hajj' },
                  { icon: 'ri-map-pin-line', label: 'Tours' },
                ].map(s => (
                  <div key={s.label} className="hp-service-pill">
                    <i className={s.icon}></i>
                    <span>{s.label}</span>
                  </div>
                ))}
              </div>
              <div className="hp-trust-row">
                <span className="hp-trust-badge"><i className="ri-check-double-line"></i> IATA Accredited</span>
                <span className="hp-trust-badge"><i className="ri-check-double-line"></i> 130+ Airlines</span>
                <span className="hp-trust-badge"><i className="ri-check-double-line"></i> 24/7 Support</span>
                <span className="hp-trust-badge"><i className="ri-check-double-line"></i> Instant Confirmation</span>
              </div>
            </Col>

            {/* Right: Login Card */}
            <Col lg={5} md={8} className="mx-auto mt-5 mt-lg-0">
              <div className="hp-login-card">
                <div className="hp-login-card-header">
                  <p>B2B Agent Portal &mdash; Authorised Travel Agents Only</p>
                </div>
                <form onSubmit={handleSubmit} autoComplete="off" noValidate>
                  <div className="mb-3">
                    <label className="form-label hp-form-label">Username</label>
                    <div className="hp-input-group">
                      <i className="ri-user-line hp-input-icon"></i>
                      <input
                        className={'form-control hp-form-input' + applyErrorClass('username')}
                        name="username"
                        type="text"
                        value={values.username}
                        onChange={handleInputChange}
                        placeholder="Enter your username"
                      />
                    </div>
                    {errors.username === false && <div className="validationerror">Please enter username</div>}
                  </div>
                  <div className="mb-4">
                    <label className="form-label hp-form-label">Password</label>
                    <div className="hp-input-group">
                      <i className="ri-lock-line hp-input-icon"></i>
                      <input
                        className={'form-control hp-form-input' + applyErrorClass('password')}
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={values.password}
                        onChange={handleInputChange}
                        placeholder="Enter your password"
                      />
                      <span className="hp-pw-toggle" onClick={() => setShowPassword(p => !p)}>
                        <i className={showPassword ? 'ri-eye-fill' : 'ri-eye-off-fill'}></i>
                      </span>
                    </div>
                    {errors.password === false && <div className="validationerror">Please enter password</div>}
                  </div>
                  <button type="submit" className="btn hp-btn-login w-100" disabled={btnSubmit}>
                    {btnSubmit ? (
                      <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Signing In...</>
                    ) : (
                      <><i className="ri-login-box-line me-2"></i>Sign In to Portal</>
                    )}
                  </button>
                </form>
                <div className="hp-login-footer">
                  <Link to="/agentregister" className="hp-register-link">
                    <i className="ri-user-add-line me-1"></i>New Agent? Register Here
                  </Link>
                  <Link to="/forgot-password" className="hp-forgot-link">Forgot Password?</Link>
                </div>
              </div>
              <div className="hp-ssl-note">
                <i className="ri-lock-2-line me-1"></i>Secured with 256-bit SSL encryption
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ===== STATS BANNER ===== */}
      <section className="hp-stats-banner">
        <Container>
          <Row className="g-0">
            {keyHighlights.map((item, i) => (
              <Col md={6} lg={3} key={item.label}>
                <div className={`hp-stat-item${i < keyHighlights.length - 1 ? ' hp-stat-divider' : ''}`}>
                  <div className="hp-stat-icon"><i className={item.icon}></i></div>
                  <div className="hp-stat-value">{item.value}</div>
                  <div className="hp-stat-label">{item.label}</div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ===== SERVICES SECTION ===== */}
      <section className="hp-section hp-services-section">
        <Container>
          <div className="hp-section-header">
            <span className="hp-section-tag">What We Offer</span>
            <h2 className="hp-section-heading">Complete B2B Travel Solutions</h2>
            <p className="hp-section-desc">Everything your travel agency needs, all under one powerful platform</p>
          </div>
          <Row className="g-4">
            {services.map(s => (
              <Col md={6} lg={4} key={s.title}>
                <div className="hp-service-card">
                  <div className="hp-service-icon" style={{ background: `${s.color}18`, color: s.color }}>
                    <i className={s.icon}></i>
                  </div>
                  <h5>{s.title}</h5>
                  <p>{s.desc}</p>
                  <div className="hp-service-arrow"><i className="ri-arrow-right-line"></i></div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ===== WHY PARTNER ===== */}
      <section className="hp-section hp-why-section">
        <Container>
          <div className="hp-section-header">
            <span className="hp-section-tag">Why Choose Us</span>
            <h2 className="hp-section-heading">Why Agents Choose {DefaultData.DEFAULTBRANDNAME}</h2>
            <p className="hp-section-desc">We don't just provide inventory — we give your agency a competitive edge</p>
          </div>
          <Row className="g-4">
            {agentBenefits.map(b => (
              <Col md={6} lg={3} key={b.title}>
                <div className="hp-benefit-card">
                  <div className="hp-benefit-icon"><i className={b.icon}></i></div>
                  <h5>{b.title}</h5>
                  <p>{b.text}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="hp-section hp-steps-section">
        <Container>
          <div className="hp-section-header">
            <span className="hp-section-tag">Get Started</span>
            <h2 className="hp-section-heading">Become a Partner Agent in 3 Easy Steps</h2>
            <p className="hp-section-desc">Start accessing exclusive B2B rates in minutes</p>
          </div>
          <div className="hp-steps-row">
            {steps.map((step, i) => (
              <React.Fragment key={step.step}>
                <div className="hp-step-item">
                  <div className="hp-step-number">{step.step}</div>
                  <div className="hp-step-icon"><i className={step.icon}></i></div>
                  <h5>{step.title}</h5>
                  <p>{step.desc}</p>
                </div>
                {i < steps.length - 1 && (
                  <div className="hp-step-connector">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="text-center mt-5">
            <Link to="/agentregister" className="hp-btn-register">
              <i className="ri-user-add-line me-2"></i>Register as Travel Agent — Free
            </Link>
          </div>
        </Container>
      </section>

      {/* ===== TECHNOLOGY PARTNERS ===== */}
      <section className="hp-section hp-partners-section">
        <Container>
          <div className="hp-section-header">
            <span className="hp-section-tag">Powered By</span>
            <h2 className="hp-section-heading">Global Technology Partners</h2>
            <p className="hp-section-desc">Connected to the world's leading travel technology & GDS systems</p>
          </div>
          <div className="hp-logo-grid">
            {partners.map(p => (
              <div key={p.name} className="hp-logo-item">
                <img src={p.logo} alt={p.name} />
                <span>{p.name}</span>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ===== ACCREDITATIONS ===== */}
      <section className="hp-section hp-accreditation-section">
        <Container>
          <div className="hp-section-header">
            <span className="hp-section-tag">Trusted & Certified</span>
            <h2 className="hp-section-heading">Our Accreditations & Memberships</h2>
            <p className="hp-section-desc">Internationally recognized for quality, reliability, and professionalism</p>
          </div>
          <div className="hp-logo-grid">
            {accreditations.map(a => (
              <div key={a.name} className="hp-logo-item">
                <img src={a.logo} alt={a.name} />
                <span>{a.name}</span>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="hp-cta-section">
        <Container>
          <div className="hp-cta-card">
            <Row className="align-items-center">
              <Col lg={8}>
                <div className="hp-cta-content">
                  <h2>Ready to Grow Your Travel Business?</h2>
                  <p>
                    Join travel agents already booking with {DefaultData.DEFAULTBRANDNAME}. Get access to exclusive B2B rates,
                    a powerful booking platform, and dedicated support — all at no setup cost.
                  </p>
                  <div className="hp-cta-features">
                    <span><i className="ri-check-line"></i> No setup fees</span>
                    <span><i className="ri-check-line"></i> Instant portal access</span>
                    <span><i className="ri-check-line"></i> Dedicated agent support</span>
                    <span><i className="ri-check-line"></i> Competitive commissions</span>
                  </div>
                </div>
              </Col>
              <Col lg={4} className="text-center mt-4 mt-lg-0">
                <Link to="/agentregister" className="hp-btn-cta">
                  <i className="ri-user-add-line me-2"></i>Register Now — It's Free
                </Link>
                <div className="hp-cta-phone">
                  <p>Or call us at</p>
                  <a href={`tel:${DefaultData.DEFAULTCOMPANYPHONENUMBER}`}>
                    <i className="ri-phone-line me-1"></i>{DefaultData.DEFAULTCOMPANYPHONENUMBER}
                  </a>
                </div>
              </Col>
            </Row>
          </div>
        </Container>
      </section>

    </div>
  );
}
